import { useCallback, useEffect, useRef, useState } from "react";
import {
  createCameraRun,
  deleteCameraRun,
  getCameraMissing,
  getCameraRun,
  getCameraRuns,
  getCameraStatus,
  requestTestCapture,
  updateCameraSettings,
} from "services/cameraApi";
import { useThumbnailCache } from "hooks/useThumbnailCache";

// "Capture now" progress. After the click the page asks the (cheap) status endpoint every
// CAPTURE_POLL_MS until the photo is stored, the camera reports an error, or CAPTURE_TIMEOUT_MS passes
// (the backend gives a requested photo the same 60 s). Nothing is polled at this rate otherwise.
export const CAPTURE_POLL_MS = 2000;
export const CAPTURE_TIMEOUT_MS = 60000;
const CAPTURE_DONE_SHOWN_MS = 4000;
const CAPTURE_IDLE = { phase: "idle" };

const clampPollingMs = (intervalSeconds) =>
  Math.min(Math.max(((intervalSeconds || 3600) * 1000) / 4, 60000), 15 * 60 * 1000);

export function useCamera(macAddress) {
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [runDetail, setRunDetail] = useState(null);
  const [images, setImages] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [fastPollUntil, setFastPollUntil] = useState(0);
  // phase: idle | waiting | arrived | failed | timeout (+ startedAt, baselines, reason, imageId)
  const [capture, setCapture] = useState(CAPTURE_IDLE);
  const selectedRunRef = useRef("");
  const thumbnailUrls = useThumbnailCache(images);

  useEffect(() => {
    selectedRunRef.current = selectedRunId;
  }, [selectedRunId]);

  const loadRun = useCallback(async (runId, cursor = "", append = false) => {
    if (!runId) {
      setRunDetail(null);
      setImages([]);
      setMissing([]);
      return;
    }
    const [detail, missingResult] = await Promise.all([
      getCameraRun(runId, cursor),
      cursor ? Promise.resolve(null) : getCameraMissing(runId),
    ]);
    setRunDetail(detail.run);
    setImages((current) => (append ? [...current, ...detail.images] : detail.images));
    setNextCursor(detail.next_cursor);
    if (missingResult) setMissing(missingResult.missing_or_failed || []);
  }, []);

  const refresh = useCallback(
    async ({ quiet = false } = {}) => {
      if (!macAddress) return;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const [runResult, statusResult] = await Promise.all([
          getCameraRuns(macAddress),
          getCameraStatus(macAddress),
        ]);
        const available = runResult.runs || [];
        setRuns(available);
        setStatus(statusResult);
        const current = selectedRunRef.current;
        const selectedStillExists = available.some((run) => String(run.id) === String(current));
        const fallback = available.find((run) => run.is_active) || available[0];
        const nextRunId = selectedStillExists ? current : fallback ? String(fallback.id) : "";
        if (nextRunId !== current) setSelectedRunId(nextRunId);
        if (nextRunId) await loadRun(nextRunId);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [loadRun, macAddress]
  );

  useEffect(() => {
    setSelectedRunId("");
    selectedRunRef.current = "";
    setRuns([]);
    setStatus(null);
    setRunDetail(null);
    setImages([]);
    setMissing([]);
    setCapture(CAPTURE_IDLE);
    if (macAddress) refresh();
  }, [macAddress, refresh]);

  const selectRun = useCallback(
    async (runId) => {
      setSelectedRunId(String(runId));
      setLoading(true);
      setError(null);
      try {
        await loadRun(String(runId));
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [loadRun]
  );

  const createRun = useCallback(
    async (runId, intervalMinutes) => {
      const result = await createCameraRun(macAddress, runId, intervalMinutes);
      setFastPollUntil(Date.now() + 2 * 60 * 1000);
      await refresh({ quiet: true });
      if (result.run?.id) await selectRun(String(result.run.id));
      return result;
    },
    [macAddress, refresh, selectRun]
  );

  const requestCaptureNow = useCallback(
    async () => {
      // What "new" means is decided from a fresh status read taken just before the request - the one
      // on screen can be minutes old, and a photo that arrived since would look like the answer.
      const baseline = await getCameraStatus(macAddress);
      const result = await requestTestCapture(macAddress);
      setCapture({
        phase: "waiting",
        startedAt: Date.now(),
        baselineId: baseline.last_image?.id ?? 0,
        baselineAckAt: baseline.last_capture_ack?.at ?? null,
      });
      return result;
    },
    [macAddress]
  );

  // While a capture is pending: ask the status endpoint every few seconds (one light request, not the
  // four of refresh) for the new photo, then load it into the gallery.
  useEffect(() => {
    if (capture.phase !== "waiting" || !macAddress) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - capture.startedAt >= CAPTURE_TIMEOUT_MS) {
        setCapture((current) => (current.phase === "waiting" ? { ...current, phase: "timeout" } : current));
        return;
      }
      if (document.visibilityState === "visible") {
        try {
          const latest = await getCameraStatus(macAddress);
          if (cancelled) return;
          setStatus(latest);
          const ack = latest.last_capture_ack;
          if ((latest.last_image?.id ?? 0) > capture.baselineId) {
            await refresh({ quiet: true });
            if (!cancelled) setCapture({ phase: "arrived", imageId: latest.last_image.id });
            return;
          }
          if (ack && ack.at !== capture.baselineAckAt && ack.status && ack.status !== "ok") {
            setCapture({ phase: "failed", reason: ack.reason || ack.status });
            return;
          }
        } catch {
          // a missed poll is not a failure - try again until the timeout
        }
      }
      if (!cancelled) timer = window.setTimeout(tick, CAPTURE_POLL_MS);
    };
    timer = window.setTimeout(tick, CAPTURE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [capture, macAddress, refresh]);

  // The "photo received" notice goes away by itself.
  useEffect(() => {
    if (capture.phase !== "arrived") return undefined;
    const timer = window.setTimeout(() => setCapture(CAPTURE_IDLE), CAPTURE_DONE_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [capture]);

  const removeRun = useCallback(async () => {
    if (!selectedRunId) return;
    await deleteCameraRun(selectedRunId);
    setSelectedRunId("");
    selectedRunRef.current = "";
    await refresh({ quiet: true });
  }, [refresh, selectedRunId]);

  const loadMore = useCallback(async () => {
    if (!selectedRunId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      await loadRun(selectedRunId, nextCursor, true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingMore(false);
    }
  }, [loadRun, loadingMore, nextCursor, selectedRunId]);

  useEffect(() => {
    if (!macAddress) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        refresh({ quiet: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
    };
  }, [macAddress, refresh]);

  useEffect(() => {
    if (!macAddress || document.visibilityState === "hidden" || !navigator.onLine) {
      return undefined;
    }
    const applied =
      (status?.config?.acknowledged && status?.config?.ack_status === "ok") ||
      status?.config?.reported_match;
    const fast = Date.now() < fastPollUntil && !applied;
    const waitMs = fast
      ? 15000
      : clampPollingMs(status?.configured_interval_seconds);
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        refresh({ quiet: true });
      }
    }, waitMs);
    return () => window.clearTimeout(timer);
  }, [fastPollUntil, macAddress, refresh, status]);

  // Turn this camera's photos 180 degrees for display (or back). Stored server-side per
  // camera, so it applies for every viewer and to photos already taken.
  const setImageRotation = useCallback(
    async (imageRotationDeg) => {
      if (!macAddress) return;
      setError(null);
      try {
        await updateCameraSettings(macAddress, { imageRotationDeg });
        await refresh({ quiet: true });
      } catch (requestError) {
        setError(requestError.message);
      }
    },
    [macAddress, refresh]
  );

  return {
    runs,
    status,
    selectedRunId,
    runDetail,
    images,
    missing,
    thumbnailUrls,
    loading,
    loadingMore,
    error,
    setError,
    selectRun,
    createRun,
    requestCaptureNow,
    capture,
    removeRun,
    loadMore,
    refresh,
    setImageRotation,
    hasMore: Boolean(nextCursor),
  };
}
